/**
 * The conversation with the builder: messages, grouped work steps and request notes.
 * The steps of a running build are not shown here but in the focus point in the
 * dock (BuildFocus); when the run ends they land here as one collapsed group.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck, faCircleCheck, faCoins, faCircleQuestion, faKey, faPlug, faPlus, faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { Collapsible } from './ui';
import { stepDetail, stepLabel, StepIcon, StepList } from './activity';
import { ScrollDownButton, useChatScroll } from './scrollDown';
import { BotMessage, UserMessage } from './ChatMessage';
import { useSelectedBot } from '../../contexts/SelectedBotContext.js';

/** Messages + work-step groups; the trailing group of a running build is returned separately. */
export function groupItems(items, runningHere) {
  const out = [];
  for (const it of items) {
    if (it.kind === 'activity' && it.data.hidden) continue;
    if (it.kind === 'assistant' && !(it.data.text || '').trim()) continue;
    if (it.kind === 'activity') {
      const last = out[out.length - 1];
      if (last && last.type === 'activities') last.items.push(it);
      else out.push({ type: 'activities', items: [it] });
    } else out.push({ type: 'item', item: it });
  }
  let liveSteps = [];
  if (runningHere && out.length && out[out.length - 1].type === 'activities') liveSteps = out.pop().items;
  return { groups: out, liveSteps };
}

/**
 * `lead`: shown above the messages (Mo's welcome in the conversation it opened).
 * `live`: shown under the last message (Mo at work, MoWorking).
 * `calm`: the start page; the first message sent from it is pinned to the top at once.
 */
export default function BuildTranscript({ builder, groups, lead, live = null, calm = false }) {
  const { items, state, conversationId } = builder;
  const scroll = useChatScroll(state ? conversationId : null, [items, state?.running], calm);

  return (
    <div className="as-scroll-wrap">
      <div className="as-transcript" ref={scroll.ref} onScroll={scroll.onScroll}>
        <div className="as-transcript-inner">
          {lead}
          {groups.map((g) => (g.type === 'activities'
            ? <Activities key={`a${g.items[0].id}`} items={g.items} />
            : <Item key={g.item.id} item={g.item} />))}
          {live}
          <div ref={scroll.spacerRef} className="as-scroll-spacer" />
        </div>
      </div>
      <ScrollDownButton show={scroll.show} onClick={scroll.scrollToBottom} />
    </div>
  );
}

function Item({ item }) {
  const { t } = useTranslation();
  const d = item.data || {};
  switch (item.kind) {
    case 'user':
      if (d.system) {
        return <div className="as-system-note">{d.note_key ? t(d.note_key, { ...(d.note_params || {}), defaultValue: d.text }) : d.text}</div>;
      }
      if (d.answers_view) {
        return (
          <UserMessage>
            <p className="as-answers-head">{t('agent_studio_my_answers')}</p>
            {d.answers_view.map((a, i) => (
              <p key={i} className="as-answer-line">
                <span className="as-answer-q">{a.question}</span> → {a.answer || t('agent_studio_no_answer')}
              </p>
            ))}
          </UserMessage>
        );
      }
      return <UserMessage text={d.text} />;
    case 'assistant':
      return <BotMessage text={d.text} avatar={false} />;
    case 'questions':
      return (
        <div className="as-note as-note-question">
          <FontAwesomeIcon icon={faCircleQuestion} />
          <div>
            <span>
              {d.status === 'open' && t('agent_studio_note_question_open')}
              {d.status === 'answered' && t('agent_studio_note_question_answered')}
              {d.status !== 'open' && d.status !== 'answered' && t('agent_studio_note_question_skipped')}
            </span>
            <ul>{(d.questions || []).map((q) => <li key={q.id}>{q.question}</li>)}</ul>
          </div>
        </div>
      );
    case 'secret':
      return (
        <div className="as-note as-note-secret">
          <FontAwesomeIcon icon={faKey} />
          <span>
            {d.status === 'open' && t('agent_studio_note_secret_open', { label: d.label })}
            {d.status === 'provided' && t('agent_studio_note_secret_provided', { label: d.label })}
            {d.status !== 'open' && d.status !== 'provided' && t('agent_studio_note_secret_skipped', { label: d.label })}
          </span>
        </div>
      );
    case 'connect':
      return (
        <div className="as-note as-note-secret">
          <FontAwesomeIcon icon={faPlug} />
          <span>
            {d.status === 'open' && t('agent_studio_note_connect_open', { app: d.provider })}
            {d.status === 'done' && t('agent_studio_note_connect_done', { app: d.provider })}
            {d.status !== 'open' && d.status !== 'done' && t('agent_studio_note_connect_skipped', { app: d.provider })}
          </span>
        </div>
      );
    case 'confirm':
      if (d.status !== 'open' && d.status !== 'running') return null; // the outcome follows as a note
      return (
        <div className="as-note as-note-secret">
          <FontAwesomeIcon icon={faCircleCheck} />
          <span>{t(d.status === 'open' ? 'agent_studio_note_confirm_open' : 'agent_studio_note_confirm_running', { title: d.title })}</span>
        </div>
      );
    case 'error':
      return (
        <div className="as-note as-note-error">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{d.text_key ? t(d.text_key, { ...(d.text_params || {}), defaultValue: d.text }) : d.text}</span>
          {d.text_key === 'agent_studio_err_credits' && <AddCredits />}
        </div>
      );
    case 'usage':
      return <RunUsage data={d} />;
    default:
      return null;
  }
}

/** Under a Mo run: the AI credits it used and what is left (AI credits on). */
function RunUsage({ data }) {
  const { t, i18n } = useTranslation();
  const num = (n) => new Intl.NumberFormat(i18n.language || 'en').format(Math.round(Number(n) || 0));
  return (
    <div className="as-run-usage">
      <FontAwesomeIcon icon={faCoins} />
      <span>{data.credits === 1 ? t('agent_studio_credits_run_used_one')
        : t('agent_studio_credits_run_used', { count: num(data.credits) })}</span>
      <span className="as-run-usage-left">{t('agent_studio_credits_run_left', { count: num(Math.max(0, data.available)) })}</span>
      {data.unpaid ? <span className="as-run-usage-left">{t('agent_studio_credits_run_unpaid')}</span> : null}
      <AddCredits quiet />
    </div>
  );
}

/** To the billing page (credit packs, upgrade). `quiet`: the light one at the end of a run's usage line. */
function AddCredits({ quiet = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedBotId } = useSelectedBot();
  return (
    <button type="button" className={quiet ? 'as-run-topup' : 'as-btn-portal as-note-action'}
      onClick={() => navigate(`/billing/${selectedBotId}`)}>
      <FontAwesomeIcon icon={quiet ? faPlus : faCoins} /> {t('agent_studio_credits_add')}
    </button>
  );
}

function Activities({ items }) {
  const { t } = useTranslation();
  const done = items.filter((i) => i.data.status !== 'running').length;
  const errors = items.filter((i) => i.data.status === 'error').length;
  const last = items[items.length - 1];

  if (items.length === 1) {
    return (
      <div className="as-activity">
        <StepIcon status={last.data.status} />
        <span>{stepLabel(t, last.data)}</span>
        {last.data.detail && <span className="as-warn-text"> – {stepDetail(t, last.data)}</span>}
      </div>
    );
  }
  const header = (
    <span className="as-activity-head">
      <FontAwesomeIcon icon={faCheck} className="as-step-done" />
      <strong>{t('agent_studio_steps', { n: items.length })}</strong>
      <span>· {t('agent_studio_steps_done', { n: done })}</span>
      {errors > 0 && <span className="as-warn-text">· {t('agent_studio_steps_notes', { n: errors })}</span>}
    </span>
  );
  return (
    <div className="as-activity-group">
      <Collapsible title={header}>
        <StepList items={items} />
      </Collapsible>
    </div>
  );
}
